import { createSelector } from '@reduxjs/toolkit';
import LoadingStatus from 'enums/loadingStatus';
import { DATA_SLICE_KEY } from './dataSlice';
import { MAP_CHIPS, deriveActiveChip } from '../services/mapChipService';
import LotStatus from '../enums/lotStatus';
import { VIEW_LOT_MAP_SELECTION } from '../constants';
// VIEW_FLOOR_PLAN_DETAIL / VIEW_HOUSING_UNIT_DETAIL: not imported — the desktop routing that
// would use them is commented out below in selectLotRoutingOutcome until those views have real
// content (see plugins/floorPlanDetail.plugin.js, plugins/housingUnitDetail.plugin.js).

const selectSlice = (state) => state[DATA_SLICE_KEY];

/**
 * Reselect prop-selector: pulls the `sitePlanLotId` passed as the second argument to a selector
 * call (e.g. `selectLotMapSelectionViewModel(state, sitePlanLotId)`) so it participates in that
 * selector's memoization cache key. Shared by selectLotRoutingOutcome and
 * selectLotMapSelectionViewModel below.
 */
const selectSitePlanLotIdProp = (_, sitePlanLotId) => sitePlanLotId;

/**
 * Plain boolean, not derived from state — the caller (lotSelectedEpic)
 * already has it via closure from context.state.isMobile (set once in index.js).
 */
const selectIsMobileProp = (_, __, isMobile) => isMobile;

/**
 * @returns {object[]} Raw lots array from the data slice.
*/
export const selectLots = (state) => selectSlice(state).lots;

/**
 * @returns {object[]} Raw house plans array from the data slice.
 */
export const selectHousePlans = (state) => selectSlice(state).housePlans;

/**
 * @returns {object[]} Raw housing units array from the data slice.
 */
export const selectHousingUnits = (state) => selectSlice(state).housingUnits;

/**
 * @returns {string|null} The lotId of the currently selected lot, or null if none.
*/
export const selectSelectedLotId = (state) => selectSlice(state).viewState.activeViewContext.selectedLotId;

/**
 * @returns {string} The currently active view (e.g. 'listing').
 */
export const selectActiveView = (state) => selectSlice(state).viewState.activeView;

/**
 * @returns {string} The currently selected tab value ('floorPlans' | 'quickMoveIns').
 */
export const selectSelectedTab = (state) => selectSlice(state).viewState.selectedTab;

/**
 * @returns {object[]} viewState.viewHistory — the navigateTo/navigateBack undo stack.
 */
export const selectViewHistory = (state) => selectSlice(state).viewState.viewHistory;

/**
 * @returns {{ lotStatus: string|null }}
 */
export const selectSearchParameters = (state) => selectSlice(state).searchParameters;

/**
 * @returns {string} LoadingStatus value for the lot-map-inventory fetch.
 */
export const selectFetchLotInventoryStatus = (state) => selectSlice(state).fetchLotInventoryStatus;

/**
 * Whether the lot-map-inventory fetch has settled (either way) — until then, derived data like
 * chip lot counts read as 0 across the board simply because the data hasn't arrived, not because
 * there's genuinely nothing to show. See NHS20-8054.
 *
 * @returns {boolean}
 */
export const selectIsLotInventorySettled = createSelector(
  [selectFetchLotInventoryStatus],
  (status) => status === LoadingStatus.Succeeded.toString() || status === LoadingStatus.Failed.toString()
);

/**
 * Derives the active map chip key ('available' | 'model' | 'coming-soon' | 'sold') from
 * searchParameters. There is no standalone activeChip field in the store — see
 * redux/STORE_DESIGN.md and services/mapChipService.js.
 *
 * @returns {string}
 */
export const selectActiveChip = createSelector(
  [selectSearchParameters],
  (searchParameters) => deriveActiveChip(searchParameters)
);

/**
 * Derives an O(1) lookup map from the lots array, keyed by sitePlanLotId (the string id SVG
 * lot <path> elements use, e.g. 'g1_l140' — see services/processCommunityMap.js).
 * Memoized — recomputes only when the lots reference changes.
 *
 * Input (store):  [{ sitePlanLotId: 'g0_l7', status: 'available' }, { sitePlanLotId: 'g0_l8', status: 'sold' }, ...]
 * Output:         { 'g0_l7': 'available', 'g0_l8': 'sold', ... }
 *
 * The store holds the canonical array (preserves order, supports future add/remove operations).
 * This selector converts it to an object once so consumers can do lotStatusMap[sitePlanLotId] in
 * O(1) instead of a O(n) .find() on every SVG element during applyFilter.
 * Returns statuses only — CSS class mapping belongs in the DOM component.
 */
export const selectLotStatusMap = createSelector(
  [selectLots],
  (lots) => Object.fromEntries(lots.map((lot) => [lot.sitePlanLotId, lot.status]))
);

/**
 * O(1) lookup map for lots keyed by housingUnitId.
 * @returns {Record<string, object>} Map of housingUnitId to lot object.
 */
export const selectLotsById = createSelector(
  [selectLots],
  (lots) => Object.fromEntries(lots.map((lot) => [lot.lotId, lot]))
);


/**
 * Lots whose status matches the currently active chip, keyed by both id spaces this feature
 * needs to match against: sitePlanLotId (string, e.g. 'g1_l140' — SVG lot <path> elements use
 * this, see components/lotPolygonColors.dom.js#applyFilter) and lotId (integer — housePlans'
 * Lots[] and housingUnits' LotId reference lots by this id instead, see
 * services/processLotMapInventory.js). lotId is only populated once the lot-inventory fetch has
 * enriched the lot, so lots without it yet are excluded from the lotIds set.
 *
 * @returns {{ sitePlanLotIds: Set<string>, lotIds: Set<number> }}
 */
export const selectMatchedLotIds = createSelector(
  [selectLots, selectActiveChip],
  (lots, activeChip) => {
    const matchedLots = lots.filter((lot) => lot.status === activeChip);
    return {
      sitePlanLotIds: new Set(matchedLots.map((lot) => lot.sitePlanLotId)),
      lotIds: new Set(
        matchedLots.filter((lot) => lot.lotId != null).map((lot) => lot.lotId)
      ),
    };
  }
);

/**
 * Housing units matching the currently active chip, enriched with lot display label for
 * Quick Move-Ins card rendering — see NHS20-8054.
 * @returns {object[]}
 */
export const selectFilteredHousingUnits = createSelector(
  [selectHousingUnits, selectLotsById, selectMatchedLotIds],
  (housingUnits, lotsById, matchedLotIds) => housingUnits
    .filter((unit) => matchedLotIds.lotIds.has(unit.lotId))
    .map((unit) => ({
      ...unit,
      lotDisplayLabel: lotsById[unit.lotId]?.displayLabel ?? null,
    }))
);

/**
 * Counts lots per chip, keyed by chip key (matches MAP_CHIPS[].key, which is also the
 * raw lot status string). A lot without an assigned Floor Plan or Spec never carries one of
 * these statuses to begin with — the API/resolveLotStatus resolves it to LotStatus.NotFound
 * instead (see services/lotStatusUtils.js) — so no additional assigned-plan/spec check is
 * needed here; matching on status is sufficient. Drives both the chip badge number and, via
 * mapChips.plugin's updateCounts/fallback, chip visibility and default-selection priority
 * (Map Chips Display Logic, NHS20-8054): a chip with nothing to show must not appear.
 *
 * @returns {Record<string, number>}
 */
export const selectChipLotCounts = createSelector(
  [selectLots],
  (lots) => {
    const counts = MAP_CHIPS.reduce((acc, chip) => {
      acc[chip.key] = 0;
      return acc;
    }, {});

    lots.forEach((lot) => {
      if (lot.status in counts) {
        counts[lot.status] += 1;
      }
    });

    return counts;
  }
);


/**
 * House plans buildable on at least one lot matching the currently active chip. Drives the
 * Floor Plans tab content — see NHS20-8054. Each plan's `lotIds` is narrowed down to just the
 * matching lots, so the "N Lots Available" card badge (components/floorPlanCards.dom.js, which
 * reads `lotIds.length`) reflects lots for the active chip instead of the plan's total lot count
 * across every status.
 *
 * @returns {object[]}
 */
export const selectFilteredHousePlans = createSelector(
  [selectHousePlans, selectMatchedLotIds],
  (housePlans, matchedLotIds) => housePlans
    .map((plan) => ({
      ...plan,
      lotIds: plan.lotIds.filter((lotId) => matchedLotIds.lotIds.has(lotId)),
    }))
    .filter((plan) => plan.lotIds.length > 0)
);

/**
 * Count of house plans matching the currently active chip. Drives the Floor Plans tab badge.
 * @returns {number}
 */
export const selectHousePlanCount = createSelector(
  [selectFilteredHousePlans],
  (housePlans) => housePlans.length
);

/**
 * Count of housing units matching the currently active chip. Drives the Quick Move-Ins tab badge.
 * @returns {number}
 */
export const selectHousingUnitCount = createSelector(
  [selectFilteredHousingUnits],
  (housingUnits) => housingUnits.length
);

/**
 * Plan ids relevant to the currently selected lot, drawn from the already chip-filtered plan
 * list (selectFilteredHousePlans) rather than the lot's raw housePlanIds — keeps this in step
 * with whatever searchParameters/active chip currently narrows the data to, the same rule every
 * other display selector in this file already follows. selectLots (raw) is used only to resolve
 * the id space (sitePlanLotId string -> lotId integer), not to source the plan list itself.
 * Empty Set when nothing is selected or the lot isn't found (stale/racing state).
 *
 * @returns {Set<number>}
 */
export const selectSelectedLotHousePlanIds = createSelector(
  [selectFilteredHousePlans, selectLots, selectSelectedLotId],
  (filteredHousePlans, lots, selectedSitePlanLotId) => {
    const selectedLot = lots.find((lot) => lot.sitePlanLotId === selectedSitePlanLotId);
    if (!selectedLot) {
      return new Set();
    }
    return new Set(
      filteredHousePlans
        .filter((plan) => plan.lotIds.includes(selectedLot.lotId))
        .map((plan) => plan.planId)
    );
  }
);

/**
 * Decides which view (if any) a lot click should navigate to, based on the lot's status, plan
 * count, whether it has a housing unit built on it, whether it matches the currently active
 * chip, and device. A lot can be Available/Model with >1 plan and still not qualify if the user
 * is browsing a different chip (e.g. Sold) — those lots are greyed-out background decoration on
 * the map (see components/lotPolygonColors.dom.js#applyFilter) and clicking one must not jump
 * the user into this flow.
 *
 * Eligible statuses: Available and Model (NHS20-8238 requires covering QMI, Model Home, and
 * Floor Plan matches) — Sold and Coming Soon are handled by their own destinations elsewhere
 * (or not yet, per NHS20-8167). lotSelectedEpic calls this selector for every matched lot click
 * regardless of status; a Sold or Coming Soon lot still reaches it, it just fails
 * isEligibleStatus and returns null immediately, before any of the four outcomes below.
 *
 * Four outcomes:
 *  - housePlanIds.length > 1: VIEW_LOT_MAP_SELECTION, both desktop and mobile. In practice only
 *    reachable for Available lots — a Model lot is an already-built home (housingUnitId set),
 *    never a multi-plan buildable one (see the mutually-exclusive housePlanIds/housingUnitId
 *    note on services/processCommunityMap.js).
 *  - lot.housingUnitId is set: mobile routes to VIEW_LOT_MAP_SELECTION (a preview step, per
 *    NHS20-8238); desktop is meant to route straight to VIEW_HOUSING_UNIT_DETAIL, but that view
 *    is currently a blank scaffold (plugins/housingUnitDetail.plugin.js), so desktop no-ops
 *    (returns null) until it has real content.
 *  - housePlanIds.length === 1: mobile routes to VIEW_LOT_MAP_SELECTION (same preview step);
 *    desktop is meant to route straight to VIEW_FLOOR_PLAN_DETAIL, but that view is currently a
 *    blank scaffold (plugins/floorPlanDetail.plugin.js), so desktop no-ops (returns null) until
 *    it has real content.
 *  - anything else: null (no navigation) — each new destination plugin extends this selector
 *    with its own resolution rule rather than owning a separate routing decision.
 *
 * @param {object} state
 * @param {string} sitePlanLotId - the SVG click id (see libraries/mapLibrary/svgLotInteraction.js);
 *   named distinctly from lot.lotId (the integer field) because both are compared in this
 *   function — using the same name for both would risk matching the wrong one silently.
 * @param {boolean} isMobile - device flag; determines whether a single-result lot resolves to
 *   the lotMapSelection preview (mobile) or straight to its detail view (desktop).
 * @returns {{ view: string, context: { planId: string|null, selectedLotId: string|null,
 *   housingUnitId: number|null } } | null}
 */
export const selectLotRoutingOutcome = createSelector(
  [selectLots, selectMatchedLotIds, selectSitePlanLotIdProp, selectIsMobileProp],
  (lots, matchedLotIds, sitePlanLotId, isMobile) => {
    const lot = lots.find((candidate) => candidate.sitePlanLotId === sitePlanLotId);
    const isEligibleStatus = lot?.status === LotStatus.Available.toString() ||
      lot?.status === LotStatus.Model.toString();

    if (!lot || !isEligibleStatus || !matchedLotIds.sitePlanLotIds.has(sitePlanLotId)) {
      return null;
    }

    if (lot.housePlanIds.length > 1) {
      return {
        view: VIEW_LOT_MAP_SELECTION,
        context: { planId: null, selectedLotId: sitePlanLotId, housingUnitId: null },
      };
    }

    if (lot.housingUnitId != null) {
      if (isMobile) {
        return {
          view: VIEW_LOT_MAP_SELECTION,
          context: { planId: null, selectedLotId: sitePlanLotId, housingUnitId: null },
        };
      }
      // VIEW_HOUSING_UNIT_DETAIL is still a blank scaffold (plugins/housingUnitDetail.plugin.js)
      // — restore this once it has real content:
      // return {
      //   view: VIEW_HOUSING_UNIT_DETAIL,
      //   context: { planId: null, selectedLotId: null, housingUnitId: lot.housingUnitId },
      // };
      return null;
    }

    if (lot.housePlanIds.length === 1) {
      if (isMobile) {
        return {
          view: VIEW_LOT_MAP_SELECTION,
          context: { planId: null, selectedLotId: sitePlanLotId, housingUnitId: null },
        };
      }
      // VIEW_FLOOR_PLAN_DETAIL is still a blank scaffold (plugins/floorPlanDetail.plugin.js)
      // — restore this once it has real content:
      // return {
      //   view: VIEW_FLOOR_PLAN_DETAIL,
      //   context: { planId: lot.housePlanIds[0], selectedLotId: sitePlanLotId, housingUnitId: null },
      // };
      return null;
    }

    return null;
  }
);

/**
 * View model for the lotMapSelection panel: the lot's group name and display label (e.g.
 * "Group 5", "56" → header renders "Group 5 Lot 56"), its buildable plans, plan count, and
 * lowest base price (reusing each plan's own pre-formatted `formattedPrice` rather than
 * reformatting a number here). Returns empty/null defaults when the lot isn't found
 * (stale/racing state) instead of throwing.
 *
 * @param {object} state
 * @param {string} sitePlanLotId - the SVG click id, not the integer lot.lotId (see
 *   selectLotRoutingOutcome above for why this parameter keeps its own distinct name)
 * @returns {{ lotGroupName: string|null, displayLabel: string|null, plans: object[],
 *   planCount: number, lowestFormattedPrice: string|null, housingUnit: object|null }}
 */
export const selectLotMapSelectionViewModel = createSelector(
  [selectLots, selectFilteredHousePlans, selectFilteredHousingUnits, selectSitePlanLotIdProp],
  (lots, filteredHousePlans, filteredHousingUnits, sitePlanLotId) => {
    const lot = lots.find((candidate) => candidate.sitePlanLotId === sitePlanLotId);
    if (!lot) {
      return {
        lotGroupName: null, displayLabel: null, plans: [], planCount: 0,
        lowestFormattedPrice: null, housingUnit: null,
      };
    }

    if (lot.housingUnitId != null) {
      const housingUnit = filteredHousingUnits.find((unit) => unit.lotId === lot.lotId) ?? null;
      return {
        lotGroupName: lot.lotGroupName,
        displayLabel: lot.displayLabel,
        plans: [],
        planCount: 0,
        lowestFormattedPrice: housingUnit?.formattedPrice ?? null,
        housingUnit,
      };
    }

    const plans = filteredHousePlans.filter((plan) => lot.housePlanIds.includes(plan.planId));
    const lowestPricedPlan = plans.reduce(
      (lowest, plan) => (plan.price != null && (lowest === null || plan.price < lowest.price) ? plan : lowest),
      null
    );
    return {
      lotGroupName: lot.lotGroupName,
      displayLabel: lot.displayLabel,
      plans,
      planCount: plans.length,
      lowestFormattedPrice: lowestPricedPlan?.formattedPrice ?? null,
      housingUnit: null,
    };
  }
);
